import React, { useState, useEffect } from 'react';
import { createResource, updateResource } from '../../services/dataService';
import config from '../../config';
import {
  XMarkIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UserIcon,
  CheckCircleIcon,
  CreditCardIcon,
  DocumentTextIcon,
  TagIcon,
  BanknotesIcon,
  ReceiptPercentIcon,
  ClipboardDocumentIcon
} from '@heroicons/react/24/outline';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Grid,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  FormLabel,
  InputAdornment
} from '@mui/material';

const TransactionDialog = ({ 
  isOpen, 
  onClose, 
  transaction = null, 
  clients = [],
  reservations = [],
  onSave,
  settings = { taxRate: 19, defaultCurrency: 'DZD' }
}) => {
  const [formData, setFormData] = useState({
    type: 'Recette',
    categorie: 'Réservation',
    clientId: '',
    description: '',
    montant: 0,
    devise: 'DZD',
    methode: 'Virement bancaire',
    statut: 'En attente',
    notes: '',
    enableTVA: true,
    customTVARate: settings.taxRate,
    enableCommission: true,
    customCommissionRate: 10,
    reservationId: '',
    date: new Date().toISOString().split('T')[0]
  });

  // Reset form when dialog opens/closes or transaction changes
  useEffect(() => {
    if (isOpen) {
      if (transaction) {
        // Edit mode - fill form with transaction data
        setFormData({
          type: transaction.type || 'Recette',
          categorie: transaction.categorie || 'Réservation',
          clientId: transaction.clientId || '',
          description: transaction.description || '',
          montant: transaction.montant || 0,
          devise: transaction.devise || 'DZD',
          methode: transaction.methode || 'Virement bancaire',
          statut: transaction.statut || 'En attente',
          notes: transaction.notes || '',
          enableTVA: transaction.taxes > 0,
          customTVARate: settings.taxRate,
          enableCommission: transaction.commission > 0,
          customCommissionRate: transaction.commission ? (transaction.commission / transaction.montant) * 100 : 10,
          reservationId: transaction.reservationId || '',
          date: transaction.date || new Date().toISOString().split('T')[0]
        });
      } else {
        // Create mode - reset form to defaults
        setFormData({
          type: 'Recette',
          categorie: 'Réservation',
          clientId: '',
          description: '',
          montant: 0,
          devise: 'DZD',
          methode: 'Virement bancaire',
          statut: 'En attente',
          notes: '',
          enableTVA: true,
          customTVARate: settings.taxRate,
          enableCommission: true,
          customCommissionRate: 10,
          reservationId: '',
          date: new Date().toISOString().split('T')[0]
        });
      }
    }
  }, [isOpen, transaction, settings.taxRate]);

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Calculate tax and commission based on custom rates and enable/disable settings
    const taxRate = formData.enableTVA ? formData.customTVARate : 0;
    const commissionRate = formData.enableCommission && formData.type === 'Recette' ? formData.customCommissionRate : 0;
    
    // Handle currency conversion
    let montantDZD = parseFloat(formData.montant);
    let tauxDeChange = null;
    let dateConversion = null;
    
    // If foreign currency is used, calculate DZD equivalent
    if (formData.devise !== 'DZD' && settings.exchangeRates && settings.exchangeRates[formData.devise]) {
      tauxDeChange = settings.exchangeRates[formData.devise].rate;
      montantDZD = montantDZD * tauxDeChange;
      dateConversion = new Date().toISOString();
    }
    
    // Use the appropriate amount for tax and commission calculations
    const baseAmount = formData.devise === 'DZD' ? montantDZD : montantDZD;
    
    // Create a simplified transaction object with only necessary fields
    const transactionData = {
      type: formData.type,
      categorie: formData.categorie,
      clientId: formData.clientId || null,
      description: formData.description,
      montant: parseFloat(formData.montant),
      devise: formData.devise,
      methode: formData.methode,
      statut: formData.statut,
      notes: formData.notes,
      dateTransaction: formData.date || new Date().toISOString().split('T')[0],
      reference: transaction?.reference || `REF-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
      montantHT: parseFloat(formData.montant),
      montantTTC: formData.enableTVA ? baseAmount * (1 + taxRate / 100) : baseAmount,
      taxes: formData.enableTVA ? baseAmount * (taxRate / 100) : 0,
      commission: formData.enableCommission && formData.type === 'Recette' ? baseAmount * (commissionRate / 100) : 0
    };
    
    try {
      let result;
      const apiUrl = `${config.financesApiUrl}/transactions`;
      
      console.log('Sending transaction data:', JSON.stringify(transactionData, null, 2));
      
      if (transaction) {
        // Update existing transaction
        const response = await fetch(`${apiUrl}/${transaction.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(transactionData)
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Error updating transaction: ${response.statusText}. Details: ${errorText}`);
        }
        
        result = await response.json();
        result = result.data;
      } else {
        // Create new transaction
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(transactionData)
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Error creating transaction: ${response.statusText}. Details: ${errorText}`);
        }
        
        result = await response.json();
        result = result.data;
      }
      
      console.log('Transaction saved successfully:', result);
      
      // Call onSave callback with saved transaction
      if (onSave) {
        onSave(result);
      }
      
      // Close dialog
      onClose();
    } catch (error) {
      console.error('Error saving transaction:', error);
      alert(`Error saving transaction: ${error.message}`);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 3,
          background: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }
      }}
    >
      <DialogTitle sx={{ color: 'white' }}>
        {transaction ? 'Modifier la transaction' : 'Nouvelle transaction'}
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Transaction Type */}
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel 
                  component="legend" 
                  sx={{ 
                    color: 'rgba(255, 255, 255, 0.7) !important',
                    mb: 1,
                    '&.Mui-focused': {
                      color: 'rgba(255, 255, 255, 0.9) !important'
                    },
                    '&.MuiFormLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7) !important'
                    }
                  }}
                >
                  Type de transaction
                </FormLabel>
                <RadioGroup
                  row
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      color: 'rgba(255, 255, 255, 0.7) !important'
                    },
                    '& .MuiRadio-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-checked': {
                        color: 'white'
                      }
                    }
                  }}
                >
                  <FormControlLabel value="Recette" control={<Radio />} label="Recette" />
                  <FormControlLabel value="Dépense" control={<Radio />} label="Dépense" />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Transaction Category */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Catégorie</InputLabel>
                <Select
                  value={formData.categorie}
                  label="Catégorie"
                  onChange={(e) => setFormData({...formData, categorie: e.target.value})}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  <MenuItem value="Réservation">Réservation</MenuItem>
                  <MenuItem value="Service">Service</MenuItem>
                  <MenuItem value="Matériel">Matériel</MenuItem>
                  <MenuItem value="Salaire">Salaire</MenuItem>
                  <MenuItem value="Loyer">Loyer</MenuItem>
                  <MenuItem value="Autre">Autre</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Client/Fournisseur */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>
                  {formData.type === 'Recette' ? 'Client' : 'Fournisseur'}
                </InputLabel>
                <Select
                  value={formData.clientId}
                  label={formData.type === 'Recette' ? 'Client' : 'Fournisseur'}
                  onChange={(e) => setFormData({...formData, clientId: e.target.value})}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  <MenuItem value="">Sélectionner...</MenuItem>
                  {clients.map(client => (
                    <MenuItem key={client.id} value={client.id}>{client.nom}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Reservation */}
            {formData.categorie === 'Réservation' && (
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel sx={{ 
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-focused': {
                      color: 'rgba(255, 255, 255, 0.9)'
                    }
                  }}>Réservation liée</InputLabel>
                  <Select
                    value={formData.reservationId}
                    label="Réservation liée"
                    onChange={(e) => setFormData({...formData, reservationId: e.target.value})}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          background: 'rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(20px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: 2,
                          mt: 1,
                          '& .MuiMenuItem-root': {
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            },
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.3)'
                              }
                            }
                          }
                        }
                      }
                    }}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '& .MuiSelect-icon': {
                        color: 'rgba(255, 255, 255, 0.7)'
                      }
                    }}
                  >
                    <MenuItem value="">Sélectionner...</MenuItem>
                    {reservations.map(reservation => (
                      <MenuItem key={reservation.id} value={reservation.id}>
                        {reservation.id} - {reservation.clientNom || 'Client inconnu'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Description de la transaction"
                sx={{
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>

            {/* Amount */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Montant"
                value={formData.montant}
                onChange={(e) => setFormData({...formData, montant: parseFloat(e.target.value) || 0})}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              />
            </Grid>

            {/* Currency */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Devise</InputLabel>
                <Select
                  value={formData.devise}
                  label="Devise"
                  onChange={(e) => setFormData({...formData, devise: e.target.value})}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  <MenuItem value="DZD">DZD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Date */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  }
                }}
              />
            </Grid>

            {/* Payment Method */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Méthode de paiement</InputLabel>
                <Select
                  value={formData.methode}
                  label="Méthode de paiement"
                  onChange={(e) => setFormData({...formData, methode: e.target.value})}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  <MenuItem value="Virement bancaire">Virement bancaire</MenuItem>
                  <MenuItem value="Carte bancaire">Carte bancaire</MenuItem>
                  <MenuItem value="Espèces">Espèces</MenuItem>
                  <MenuItem value="Chèque">Chèque</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Status */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: 'rgba(255, 255, 255, 0.9)'
                  }
                }}>Statut</InputLabel>
                <Select
                  value={formData.statut}
                  label="Statut"
                  onChange={(e) => setFormData({...formData, statut: e.target.value})}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        mt: 1,
                        '& .MuiMenuItem-root': {
                          color: 'white',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)'
                            }
                          }
                        }
                      }
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255, 255, 255, 0.7)'
                    }
                  }}
                >
                  <MenuItem value="En attente">En attente</MenuItem>
                  <MenuItem value="Terminé">Terminé</MenuItem>
                  <MenuItem value="Annulé">Annulé</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* VAT */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.enableTVA}
                    onChange={(e) => setFormData({...formData, enableTVA: e.target.checked})}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-checked': {
                        color: 'white'
                      }
                    }}
                  />
                }
                label="Appliquer la TVA"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    color: 'rgba(255, 255, 255, 0.7) !important'
                  }
                }}
              />
              {formData.enableTVA && (
                <TextField
                  type="number"
                  value={formData.customTVARate}
                  onChange={(e) => setFormData({...formData, customTVARate: parseFloat(e.target.value) || 0})}
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end" sx={{ color: 'rgba(255, 255, 255, 0.7) !important' }}>%</InputAdornment>
                  }}
                  sx={{
                    width: 100,
                    mt: 1,
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'white'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    }
                  }}
                />
              )}
            </Grid>

            {/* Commission */}
            {formData.type === 'Recette' && (
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.enableCommission}
                      onChange={(e) => setFormData({...formData, enableCommission: e.target.checked})}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&.Mui-checked': {
                          color: 'white'
                        }
                      }}
                    />
                  }
                  label="Appliquer une commission"
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      color: 'rgba(255, 255, 255, 0.7) !important'
                    }
                  }}
                />
                {formData.enableCommission && (
                  <TextField
                    type="number"
                    value={formData.customCommissionRate}
                    onChange={(e) => setFormData({...formData, customCommissionRate: parseFloat(e.target.value) || 0})}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end" sx={{ color: 'rgba(255, 255, 255, 0.7) !important' }}>%</InputAdornment>
                    }}
                    sx={{
                      width: 100,
                      mt: 1,
                      '& .MuiOutlinedInput-root': { 
                        borderRadius: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white'
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      }
                    }}
                  />
                )}
              </Grid>
            )}

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Notes additionnelles..."
                sx={{
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1
                  }
                }}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={onClose}
          sx={{ borderRadius: 2, color: 'white' }}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          variant="contained"
          onClick={handleSubmit}
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
            color: 'white'
          }}
        >
          {transaction ? 'Modifier' : 'Créer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionDialog;
